// =============================================================================
// friendlyError — UX audit §5.6: stop showing operators SQL.
//
// 316 sites interpolate raw `error.message`; the operator's two most common
// failures — an RLS denial and a duplicate key — arrive as
// `[23505] duplicate key value violates unique constraint …`. This maps a
// thrown unknown to an operator sentence, keyed on PG codes and constraint
// names, with the raw driver text preserved for a "Details" disclosure.
// Generalises PieceworkCapture's FRIENDLY_TALLY_MESSAGES mechanism (kept
// there as a `domain` registry example — screen-specific RAISE text stays at
// the screen, this file owns what's true platform-wide).
//
// Wrapped errors are the norm, not the edge: the 23505→ValidationError sweep
// found wrapper `Error`s DESTROY the `.code`, so every code branch also has a
// text-pattern fallback that matches on the message alone.
// =============================================================================

import { ValidationError, isNetworkFailure } from '@/lib/errorReporter'

export interface Friendly {
  /** Operator-facing sentence. */
  message: string
  /** Raw driver text for a "Details" disclosure; null when `message` already
   *  carries everything worth showing (our own RAISEs, unknowns). */
  detail: string | null
  /** PG / PostgREST code when one survived the throw path. */
  code: string | null
}

// Constraint-name → sentence. Populate as real constraint hits surface —
// a named entry beats the generic duplicate/linked sentence below.
const CONSTRAINT_MESSAGES: Record<string, string> = {
  // mig 20261327000000 — one live grower per name per tenant (U26 D1). Rename, restore and the creators all hit it.
  growers_name_per_tenant_uniq:
    'A grower with this name already exists. Use that grower, or merge the two.',
  // mig 20261174500000 — one live irrigation system per farm per export code
  // (G1, G2). Both Add system and Edit system can hit it.
  irrigation_controllers_farm_system_uidx:
    'Another system on this farm already has that code. Each system in a pump room needs its own code (G1, G2…).',
  // mig 20261128000000 — a temperature reading is corrected at most once.
  coldroom_temp_readings_once_corrected:
    'This reading was already corrected. Refresh to see the correction.',
  // A second correction of the same reading lands on the same slot (original + 1 s), so the slot key
  // fires before the once-corrected index; it means the same thing to the person.
  coldroom_temp_readings_slot_uniq:
    'This reading was already corrected. Refresh to see the correction.',
  // mig 20261016193000 — one drain reading per station per day (equip-05
  // backstop; the capture INSERT is plain, so a double-capture lands here).
  drain_measurements_station_date_uidx:
    'A reading for this station and date already exists — refresh the list and edit that one instead of capturing it again.',
  // mig 20261016194000 — one live receipt per supplier + delivery note
  // (inputs-01: the gate capture and the office re-capture used to both land,
  // doubling stock, FIFO batches and account spend).
  goods_receipts_supplier_dn_uidx:
    'A receipt from this supplier with this delivery note number is already captured — search the register for the DN and edit that one instead of capturing it again.',
  // mig 1017 — supplier invoice double-capture guard (inputs-02: the exact
  // repeat used to surface as the raw 23505 text).
  supplier_expense_invoices_uidx:
    'This supplier invoice number is already captured for this supplier — find it under the invoices list instead of capturing it again.',
  // mig 064 — a mission's truck type is REEFER, TAUT or not set (the rate-card
  // pair). A registry vehicle's free-text type ("FLATBED") copied as typed
  // used to surface as the raw 23514 text (missionTruckType.ts).
  loading_missions_truck_type_check:
    'A mission\'s truck type can only be REEFER or TAUT (or not set). Pick one of those under Truck type and save again.',
  // mig 20261037500000 — two businesses can never share a staff-login label
  // (the slug reduced to [a-z0-9]: 'kf49' and 'kf-49' are one). The tenant-
  // creating edge functions answer it with their own sentence naming the slug
  // (supabase/functions/_shared/loginLabelClash.ts); this catches a path that
  // reaches a screen raw, e.g. a function not yet redeployed.
  tenants_login_label_key:
    'That slug is too close to an existing business\'s: their staff logins would clash. Pick a different slug.',
  // mig 20261037500000 — CHECK tenants_slug_plain: a slug is plain ASCII
  // letters, digits, '_' and '-', never empty (23514). The tenant-creating
  // functions clean a slug to [a-z0-9-] before they insert, so this catches a
  // path that reaches a screen raw (New tenant, Signup requests' approve), e.g.
  // a function not yet redeployed.
  tenants_slug_plain:
    'That web address (slug) can\'t be used: it must not be empty, and may hold only plain letters, numbers, dashes and underscores (no spaces, dots or accents). Pick a different slug.',
}

// Constraints whose 23505 `details` names ANOTHER business's data (the label
// key of the business that already holds it): the sentence only, no Details.
const DETAIL_WITHHELD = new Set(['tenants_login_label_key'])

interface ErrShape { message: string; details: string | null; hint: string | null; code: string | null }

// PostgrestError isn't reliably `instanceof Error` (varies by SDK version),
// and app code often re-wraps it — read every shape we've met in the wild.
function readShape(err: unknown): ErrShape {
  if (err == null) return { message: '', details: null, hint: null, code: null }
  if (typeof err === 'string') return { message: err, details: null, hint: null, code: null }
  const e = err as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown }
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null)
  const message = str(e.message) ?? (err instanceof Error ? err.message : '') ?? ''
  // A "[23505] …" prefix (SubmitOverlay's own format, and several wrappers)
  // is a recoverable code even when the `.code` property was destroyed.
  const bracket = /^\[(\w{5,8})\]\s/.exec(message)
  return {
    message,
    details: str(e.details),
    hint:    str(e.hint),
    code:    str(e.code) ?? (bracket ? bracket[1] : null),
  }
}

function rawText(s: ErrShape): string {
  const parts = [s.message, s.details, s.hint ? `(hint: ${s.hint})` : null].filter(Boolean)
  const text = parts.join(' · ') || 'Something went wrong'
  return s.code && !text.startsWith(`[${s.code}]`) ? `[${s.code}] ${text}` : text
}

// `Key (grower_id, name)=(…, Foo)` from a 23505/23503 detail → "grower_id, name".
function keyFields(details: string | null): string | null {
  const m = details ? /Key \(([^)]+)\)=/.exec(details) : null
  return m ? m[1] : null
}

function constraintName(s: ErrShape): string | null {
  const m = /(?:unique|foreign key|check) constraint "([^"]+)"/.exec(`${s.message} ${s.details ?? ''}`)
  return m ? m[1] : null
}

// fallow-ignore-next-line complexity
export function friendlyError(err: unknown, domain?: [RegExp, string][]): Friendly {
  const s = readShape(err)
  const raw = rawText(s)

  // Our own thrown operator text — already a sentence, never bury it.
  if (err instanceof ValidationError) return { message: s.message, detail: null, code: s.code }

  // Screen-specific registry (the FRIENDLY_TALLY_MESSAGES pattern) wins next:
  // it maps our own RAISE backstops, which are more specific than any code.
  const hit = domain?.find(([re]) => re.test(s.message))
  if (hit) {
    // `$1`, `$2` in the sentence take the pattern's capture groups (a grower's name, a count).
    const m = hit[0].exec(s.message)
    const message = hit[1].replace(/\$(\d)/g, (_, i: string) => m?.[Number(i)] ?? '')
    return { message, detail: raw, code: s.code }
  }

  // P0001 = our own RAISE EXCEPTION — written for operators at the RPC, so it
  // IS the message (a domain registry above is the escape hatch when not).
  if (s.code === 'P0001') return { message: s.message, detail: null, code: s.code }

  const named = constraintName(s)
  if (named && CONSTRAINT_MESSAGES[named]) {
    return { message: CONSTRAINT_MESSAGES[named], detail: DETAIL_WITHHELD.has(named) ? null : raw, code: s.code }
  }

  const text = `${s.message} ${s.details ?? ''}`
  const fields = keyFields(s.details)

  if (s.code === '23505' || /duplicate key value/i.test(text)) {
    return {
      message: `That would create a duplicate — a record with the same ${fields ?? 'identifying values'} already exists.`,
      detail: raw, code: s.code ?? '23505',
    }
  }
  if (s.code === '23503' || /violates foreign key constraint/i.test(text)) {
    return {
      message: 'This record is linked to other records, so the change isn\'t allowed. Remove or re-point what depends on it first.',
      detail: raw, code: s.code ?? '23503',
    }
  }
  if (s.code === '42501' || /row-level security|permission denied/i.test(text)) {
    return {
      message: 'You don\'t have permission for that action in this business. If you should, ask an admin to check your role.',
      detail: raw, code: s.code ?? '42501',
    }
  }
  if (s.code === '23502' || /null value in column/i.test(text)) {
    return { message: 'A required field is missing a value.', detail: raw, code: s.code ?? '23502' }
  }
  if (s.code === '23514' || /violates check constraint/i.test(text)) {
    return { message: 'A value doesn\'t pass one of this record\'s validation rules.', detail: raw, code: s.code ?? '23514' }
  }
  if (s.code === '22P02' || s.code === '22007' || s.code === '22008' || /invalid input syntax/i.test(text)) {
    return { message: 'One of the values isn\'t in the expected format — check numbers and dates.', detail: raw, code: s.code ?? '22P02' }
  }
  if (s.code === '40001' || s.code === '40P01' || /deadlock|could not serialize/i.test(text)) {
    return { message: 'The save clashed with another change happening at the same moment — try again.', detail: raw, code: s.code }
  }
  if (s.code === '57014' || /statement timeout|canceling statement/i.test(text)) {
    return { message: 'The server took too long and cancelled the request — try again.', detail: raw, code: s.code ?? '57014' }
  }
  if (s.code === 'PGRST116') {
    return { message: 'That record couldn\'t be found — it may have been deleted or moved.', detail: raw, code: s.code }
  }
  // The app asking this server for something it hasn't got: a migration that
  // hasn't been applied here, or a rename that shipped on one side only. It
  // arrives as a bare PG/PostgREST sentence naming a table, column or function
  // — `relation "chemical_balances" does not exist` — which is the raw string
  // operators saw most often, because the READ path rendered it whole
  // (docs/audits/FARM-UI-AUDIT-2026-09-02 §1). Nothing they can do fixes it,
  // and a Retry re-runs the same missing query, so say both.
  if (s.code === '42P01' || s.code === '42703' || s.code === '42883'
      || s.code === 'PGRST202' || s.code === 'PGRST205'
      || /\b(relation|column|table|function|type|schema)\b.*\bdoes not exist\b/i.test(text)
      || /schema cache/i.test(text)) {
    return {
      message: 'This screen is asking the server for data it doesn\'t have — usually an update that hasn\'t reached this business yet. Retrying won\'t help; report it to your administrator.',
      detail: raw, code: s.code,
    }
  }
  if (isNetworkFailure(err)) {
    return { message: 'Couldn\'t reach the server — check your connection and try again.', detail: raw, code: s.code }
  }

  // Unknown: the raw text is the best we have; don't hide it behind Details.
  return { message: raw, detail: null, code: s.code }
}

// Rethrow a failed write as a ValidationError carrying its OPERATOR sentence.
//
// `throw new ValidationError(error.message)` is how a mutationFn keeps a
// handled write failure out of Sentry (main.tsx's mutation-cache subscriber
// reports every raw mutation error). But it also LAUNDERS the raw driver text
// into a message friendlyError then passes through verbatim by design — so the
// screen's `friendlyError(e).message` in onError is powerless, and the operator
// still reads `[23505] duplicate key value violates unique constraint …`.
//
// This keeps the reporter behaviour identical and fixes only the text. Use it
// wherever the old line was `throw new ValidationError(error.message)`; keep
// `new ValidationError('…')` for prose you wrote yourself.
export function throwFriendly(err: unknown, domain?: [RegExp, string][]): never {
  throw new ValidationError(friendlyError(err, domain).message)
}
